import { SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Select } from 'antd';
import React, { useEffect, useRef } from 'react';

import { DATE_FORMAT_DISPLAY } from '@constants';
import { FieldTypes } from '@constants/formConfig';
import Utils from '@utils';

const { RangePicker } = DatePicker;

// Port từ license-cms/source/src/compoments/common/entryForm/SearchForm.js (221 dòng,
// class SearchForm extends BasicForm -> hook). Giữ NGUYÊN logic, bố cục và chuỗi hiển thị.
//
// ⚠️ KHÁC hẳn SearchForm của mreport-portal-site (510 dòng): bản license KHÔNG có
// TAG_AUTOCOMPLETE/TAG_AUTOCOMPLETE_RESTAURANT, KHÔNG dùng getPopupContainer, và hai nút
// hành động là nút CÓ CHỮ ("Tìm kiếm" / "Làm mới") chứ không phải nút chỉ có icon.
//
// i18n nguồn: t('searchForm:searchButton') = 'Tìm kiếm', t('searchForm:clearButton') = 'Làm mới',
// nhãn option đi qua t('constants:<label>', <label>) — ở bản mới đổ thẳng nhãn tiếng Việt từ
// masterData nên tra từ điển không còn cần thiết (xem MIGRATION.md).
const SearchForm = ({
    searchFields,
    className,
    hiddenAction,
    initialValues,
    allowClear,
    onSubmit,
    onResetForm,
    formRef,
}) => {
    const [form] = Form.useForm();
    const innerRef = useRef(null);

    const getPlaceHolder = (item) => {
        return item.seachPlaceholder || `Tìm kiếm bằng ${Utils.camelCaseToTitleCase(item.key)}`;
    };

    const getLabelFieldItem = (item) => {
        if (item.isShowFilterLabel) return item.title;
        return '';
    };

    // nguồn BasicForm.handleSubmit: onSubmit({ ...formValues, ...this.otherData });
    // SearchForm không gán otherData nên tương đương onSubmit(formValues).
    const handleSubmit = (formValues) => {
        onSubmit(formValues);
    };

    const onChangeAutocomplete = (value, item, fieldItem) => {
        const { onChange, reflectFields, isSubmitOnChangeValue } = fieldItem;
        if (isSubmitOnChangeValue) {
            handleSubmit(form.getFieldsValue());
        }
        if (onChange) {
            onChange(value, item);
        }
        if (reflectFields) {
            const fileds = reflectFields.split(',');
            fileds.forEach((reflectFieldName) => form.setFieldValue(reflectFieldName, undefined));
        }
    };

    const onSelectValue = (value, fieldItem) => {
        const { isSubmitOnChangeValue, onSelectValue: onSelectValueCb } = fieldItem;
        if (isSubmitOnChangeValue) {
            handleSubmit(form.getFieldsValue());
        } else if (onSelectValueCb) {
            onSelectValueCb(value);
        }
    };

    const getSelectOptions = (fieldItem) => {
        let { options, initialValue, optionValueKey, optionLabelKey } = fieldItem;
        optionValueKey = optionValueKey || 'value';
        optionLabelKey = optionLabelKey || 'label';
        if (options && options.length > 0) return options;
        else if (initialValue) return [{ [optionValueKey]: initialValue, [optionLabelKey]: '' }];
        else return [];
    };

    const onChangeDateField = (date, fieldItem) => {
        if (fieldItem.onChange) {
            fieldItem.onChange(date);
        }

        if (fieldItem.fieldNameChild) {
            form.setFieldValue(fieldItem.fieldNameChild, null);
        }
    };

    // nguồn: so khớp BỎ DẤU trên nội dung option
    const onFilterOption = (input, option) => {
        return (
            Utils.removeAccents(String(option.children).toLowerCase()).indexOf(
                Utils.removeAccents(input.toLowerCase()),
            ) >= 0
        );
    };

    const renderFormType = (fieldItem) => {
        if (fieldItem === undefined || fieldItem === null) {
            return null;
        }
        if (fieldItem.fieldType === FieldTypes.DATE) {
            const dateFormat = fieldItem.format || DATE_FORMAT_DISPLAY;
            return (
                <DatePicker
                    style={{ width: fieldItem.width || 200 }}
                    placeholder={getPlaceHolder(fieldItem)}
                    defaultValue={fieldItem.initialValue}
                    format={dateFormat}
                    onChange={(date) => onChangeDateField(date, fieldItem)}
                    disabledDate={fieldItem.disabledDate}
                />
            );
        } else if (fieldItem.fieldType === FieldTypes.DATE_RANGE) {
            const dateFormat = fieldItem.format || DATE_FORMAT_DISPLAY;
            return (
                <RangePicker
                    placeholder={getPlaceHolder(fieldItem)}
                    disabledDate={fieldItem.disabledDate}
                    style={{ width: fieldItem.width || 200 }}
                    format={dateFormat}
                    onChange={(date) => onChangeDateField(date, fieldItem)}
                    allowClear={allowClear}
                />
            );
        } else if (fieldItem.fieldType === FieldTypes.SELECT) {
            let { optionValueKey, optionLabelKey, loading, allowClear: fieldAllowClear } = fieldItem;
            optionValueKey = optionValueKey || 'value';
            optionLabelKey = optionLabelKey || 'label';

            return (
                <Select
                    showArrow
                    allowClear={fieldAllowClear === false ? false : true}
                    loading={loading}
                    placeholder={getPlaceHolder(fieldItem)}
                    style={{ width: fieldItem.width || 200 }}
                    defaultValue={fieldItem.initialValue}
                    onSelect={(value) => onSelectValue(value, fieldItem)}
                >
                    {getSelectOptions(fieldItem).map((option) => (
                        <Select.Option key={option[optionValueKey]}>{option[optionLabelKey]}</Select.Option>
                    ))}
                </Select>
            );
        } else if (fieldItem.fieldType === FieldTypes.AUTOCOMPLETE) {
            let {
                options,
                onSearch,
                optionValueKey,
                optionLabelKey,
                optionLabelProp,
                renderItem,
                loading,
                allowClear: fieldAllowClear,
            } = fieldItem;
            optionValueKey = optionValueKey || 'id';
            optionLabelKey = optionLabelKey || 'name';
            optionLabelProp = optionLabelProp || 'children';
            return (
                <Select
                    showSearch
                    showArrow={loading || false}
                    allowClear={fieldAllowClear === false ? false : true}
                    loading={loading}
                    style={{ minWidth: 200 }}
                    placeholder={getPlaceHolder(fieldItem)}
                    defaultActiveFirstOption={false}
                    onChange={(value, item) => onChangeAutocomplete(value, item, fieldItem)}
                    onSearch={onSearch}
                    filterOption={onSearch ? false : onFilterOption}
                    optionLabelProp={optionLabelProp}
                >
                    {options.map((option) =>
                        renderItem ? (
                            renderItem(option[optionValueKey], option[optionValueKey], option)
                        ) : (
                            <Select.Option key={option[optionValueKey]}>{option[optionLabelKey]}</Select.Option>
                        ),
                    )}
                </Select>
            );
        } else {
            return <Input style={{ width: fieldItem.width || 200 }} placeholder={getPlaceHolder(fieldItem)} />;
        }
    };

    // nguồn componentDidMount: this.formRef.current.setFieldsValue(this.props.initialValues)
    useEffect(() => {
        form.setFieldsValue(initialValues);
        innerRef.current = form;
        if (formRef) formRef.current = form;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // nguồn componentDidUpdate: chỉ chạy khi searchFields ĐỔI THAM CHIẾU
    useEffect(() => {
        searchFields.forEach((searchField) => {
            if (
                searchField.isSelectFirstOption &&
                searchField.options &&
                searchField.options.length > 0 &&
                !form.getFieldValue(searchField.key)
            ) {
                const optionValueKey = searchField.optionValueKey || 'id';
                form.setFieldValue(searchField.key, searchField.options[0][optionValueKey].toString());
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFields]);

    // nguồn BasicForm.handleReset
    const handleReset = () => {
        form.resetFields();
        if (onResetForm) onResetForm(innerRef);
    };

    return (
        <Form form={form} layout="inline" onFinish={handleSubmit} className={className || 'search-form'}>
            {searchFields.map((fieldItem) => (
                <Form.Item key={fieldItem.key} label={getLabelFieldItem(fieldItem)} name={fieldItem.key}>
                    {renderFormType(fieldItem)}
                </Form.Item>
            ))}
            {hiddenAction ? null : (
                <Form.Item style={{ marginTop: '3.5px' }}>
                    <Button icon={<SearchOutlined />} type="primary" htmlType="submit">
                        Tìm kiếm
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                        <i className="icfa fa-refresh" /> Làm mới
                    </Button>
                </Form.Item>
            )}
        </Form>
    );
};

export default SearchForm;
